import {
    Box,
    Button,
    Grid,
    Typography,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    IconButton,
} from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { Event, ArrowRight, ArrowLeft } from "@material-ui/icons";
import momentTZ from "moment-timezone";
import { DateTime } from "luxon";
import { Localizations } from "../../../Shared/sharedTypes";
import Loading from "../../../Shared/loading";
import { Order } from "../../../Shared/GeneratedTypes";

interface propsType {
    scheduleSlots: DateTime[] | null;
    setSchedule: (scheduleTime: DateTime, startNow: boolean) => void;
    localizations: Localizations;
    scheduledDate: DateTime | null;
    getScheduleSlots: (start: DateTime, end: DateTime) => void;
    preventSameDayScheduling: boolean;
    order: Order;
}
export default function Scheduling({
    scheduleSlots,
    setSchedule,
    localizations,
    scheduledDate,
    getScheduleSlots,
    preventSameDayScheduling,
    order,
}: propsType) {
    //We trigger this earlier in life cycle when not already scheduled.  But on re-schedule possible we need to fire
    useEffect(() => {
        if (!!scheduledDate && scheduleSlots === null) {
            getScheduleSlots(
                preventSameDayScheduling ? DateTime.local().startOf("day").plus({ days: 1 }) : DateTime.local(),
                DateTime.local().plus({ days: 30 })
            );
        }
    }, []);

    const [timeZone, setTimeZone] = useState<string>(momentTZ.tz.guess());

    const [date, setDate] = useState<DateTime>(
        preventSameDayScheduling ? DateTime.local().startOf("day").plus({ days: 1 }) : DateTime.local()
    );

    const [timeSlotNoLongerAvailable, setTimeSlotNoLongerAvailable] = useState<boolean>(false);

    //Filter to today
    var slots = (scheduleSlots ?? []).filter((d) => d.hasSame(date, "day"));
    var isToday = date.hasSame(DateTime.local(), "day");
    //Check for slot +/- 1 hr from now
    var startNow = isToday && slots.find((d) => Math.abs(d.diff(date, "hours").hours) <= 1);
    //Filter to items > now
    slots = isToday ? slots.filter((d) => d.diff(date, "hours").hours > 1) : slots;

    function setScheduleDate(scheduleTime: DateTime, startNow: boolean) {
        var timeSlotNoLongerAvailable =
            preventSameDayScheduling && scheduleTime.toJSDate().toDateString() == new Date().toDateString();
        setTimeSlotNoLongerAvailable(timeSlotNoLongerAvailable);
        if (!timeSlotNoLongerAvailable) {
            setSchedule(scheduleTime, startNow);
        }
    }

    var theme = useTheme();

    var options = momentTZ.tz.names().map((z) => (
        <option key={z} value={z}>
            {z.replace("_", " ")}
        </option>
    ));
    var slotButtons = slots.map((s) => (
        <Button
            fullWidth
            style={{ marginBottom: theme.spacing(2) }}
            key={s.toString()}
            color="primary"
            variant="outlined"
            onClick={() => setScheduleDate(s, false)}
        >
            {s.setZone(timeZone).toLocaleString(DateTime.TIME_WITH_SHORT_OFFSET)}
        </Button>
    ));

    if (scheduleSlots === null) {
        return <Loading></Loading>;
    }

    return (
        <>
            <Grid item className="PhaseTopSection" style={{ display: "flex", flexDirection: "column" }}>
                <Box m={3} mb={2}>
                    <Typography variant="h5">{localizations.chooseYourTime}</Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <IconButton
                            disabled={
                                isToday ||
                                (preventSameDayScheduling && date.minus({ days: 1 }).hasSame(DateTime.local(), "day"))
                            }
                            onClick={() => setDate(date.minus({ days: 1 }))}
                        >
                            <ArrowLeft />
                        </IconButton>
                        <Box textAlign="center">
                            <Event
                                fontSize="inherit"
                                style={{ fontSize: "50px", color: theme.palette.primary.main }}
                            ></Event>
                            <Typography style={{ color: theme.palette.primary.main, fontSize: "1.7em" }}>
                                {date.toLocaleString({ weekday: "long" })}
                            </Typography>
                            <Typography style={{ color: theme.palette.text.primary, fontSize: "1em" }}>
                                {date.toLocaleString(DateTime.DATE_MED)}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setDate(date.plus({ days: 1 }))}>
                            <ArrowRight></ArrowRight>
                        </IconButton>
                    </Box>
                    <FormControl fullWidth style={{ padding: 10 }}>
                        <InputLabel htmlFor="timeZone">{localizations.timeZone}</InputLabel>
                        <Select
                            native
                            fullWidth
                            value={timeZone}
                            onChange={(e: React.ChangeEvent<{ name?: string; value: unknown }>) =>
                                setTimeZone(e.target.value as string)
                            }
                            inputProps={{
                                name: "timeZone",
                                id: "timeZone",
                            }}
                        >
                            {options}
                        </Select>
                    </FormControl>
                    {isToday && order.isLC360 && (
                        <>
                            <Typography
                                style={{
                                    margin: theme.spacing(2),
                                    color: theme.palette.info.main,
                                    fontSize: "1em",
                                }}
                            >
                                {localizations.availableTimesInfoLC360}
                            </Typography>
                        </>
                    )}
                    {isToday && !order.isLC360 && (
                        <>
                            <Typography
                                style={{
                                    margin: theme.spacing(2),
                                    color: theme.palette.info.main,
                                    fontSize: "1em",
                                }}
                            >
                                {localizations.availableTimesInfoNonLC360}
                            </Typography>
                        </>
                    )}
                    {isToday && (
                        <>
                            {startNow && !preventSameDayScheduling && (
                                <Button
                                    fullWidth
                                    color="primary"
                                    variant="contained"
                                    onClick={() => {
                                        var time = DateTime.local().toUTC();
                                        //For now we just force the schedule to the top of the current hour
                                        time = time.set({
                                            minute: 0,
                                            second: 0,
                                            millisecond: 0,
                                        });
                                        setScheduleDate(time, true);
                                    }}
                                >
                                    {localizations.startNow}
                                </Button>
                            )}
                            {(slots.length > 0 || timeSlotNoLongerAvailable) && (
                                <Typography
                                    align="center"
                                    style={{ margin: theme.spacing(2), fontWeight: "bold", fontSize: "1.3em" }}
                                >
                                    {timeSlotNoLongerAvailable
                                        ? localizations.timeSlotsNoLongerAvailable
                                        : localizations.moreAvailableTimesToday}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
                <Box mr={3} ml={3} mb={3} style={{ flex: 1, position: "relative" }}>
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            overflow: "auto",
                        }}
                    >
                        {slots.length > 0 && slotButtons}
                        {slots.length === 0 && !startNow && (
                            <Box p={3} textAlign="center">
                                <Typography variant="h5">
                                    {localizations.noScheduleSlots ??
                                        "There are currently no available time slots on this day. Please select another day using the picker above."}
                                </Typography>
                            </Box>
                        )}
                    </div>
                </Box>
            </Grid>
        </>
    );
}
